import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ListResultDto } from '@abp/ng.core';
import { Observable } from 'rxjs';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import { MyScheduledCallDto } from '../models/my-scheduled-call.dto';
import { ReportScheduledCallFailedDto } from '../models/report-scheduled-call-failed.dto';
import { ScheduledCallBeginCallDto } from '../models/scheduled-call-begin-call.dto';
import { ScheduledCallOfferActionResultDto } from '../models/scheduled-call-offer-action-result.dto';
import { ScheduledCallOfferDto } from '../models/scheduled-call-offer.dto';

const SCHEDULED_CALL_AGENT = `${API_PREFIX}/scheduled-call-agent`;

/** Agent Desktop callback offer endpoints; every call hides global error toasts (154880/5.4). */
@Injectable({ providedIn: 'root' })
export class ScheduledCallAgentClientService {
  private readonly http = inject(HttpClient);
  private readonly options = { headers: new HttpHeaders({ 'hide-errors': 'true' }) };

  /** Returns the open offer, a newly claimed one, or `null` (204) when there is nothing to offer. */
  claimNext(): Observable<ScheduledCallOfferDto | null> {
    return this.http.post<ScheduledCallOfferDto | null>(
      resolveUrl(`${SCHEDULED_CALL_AGENT}/claim-next`),
      null,
      this.options,
    );
  }

  getMyOffer(): Observable<ScheduledCallOfferDto | null> {
    return this.http.get<ScheduledCallOfferDto | null>(
      resolveUrl(`${SCHEDULED_CALL_AGENT}/my-offer`),
      this.options,
    );
  }

  getMyUpcoming(): Observable<ListResultDto<MyScheduledCallDto>> {
    return this.http.get<ListResultDto<MyScheduledCallDto>>(
      resolveUrl(`${SCHEDULED_CALL_AGENT}/my-upcoming`),
      this.options,
    );
  }

  beginCall(attemptId: number): Observable<ScheduledCallBeginCallDto> {
    return this.http.post<ScheduledCallBeginCallDto>(
      this.attemptUrl(attemptId, 'begin-call'),
      null,
      this.options,
    );
  }

  abandon(attemptId: number): Observable<ScheduledCallOfferActionResultDto> {
    return this.postAction(attemptId, 'abandon');
  }

  timeout(attemptId: number): Observable<ScheduledCallOfferActionResultDto> {
    return this.postAction(attemptId, 'timeout');
  }

  reportCallFailed(
    attemptId: number,
    input: ReportScheduledCallFailedDto,
  ): Observable<ScheduledCallOfferActionResultDto> {
    return this.http.post<ScheduledCallOfferActionResultDto>(
      this.attemptUrl(attemptId, 'report-call-failed'),
      input,
      this.options,
    );
  }

  release(attemptId: number): Observable<ScheduledCallOfferActionResultDto> {
    return this.postAction(attemptId, 'release');
  }

  private postAction(
    attemptId: number,
    action: string,
  ): Observable<ScheduledCallOfferActionResultDto> {
    return this.http.post<ScheduledCallOfferActionResultDto>(
      this.attemptUrl(attemptId, action),
      null,
      this.options,
    );
  }

  private attemptUrl(attemptId: number, action: string): string {
    return resolveUrl(`${SCHEDULED_CALL_AGENT}/${attemptId}/${action}`);
  }
}
